import { createFileRoute } from "@tanstack/react-router";
import type { Filter } from "applesauce-core/helpers/filter";
import { useMemo, useRef, useState } from "react";
import { useCurrentAccount } from "../lib/nostr/auth";
import {
  getSongListSongCount,
  parseSongEvent,
  SONG_KIND,
  songListHasSong,
  type ParsedSong,
  type ParsedSongList,
} from "../lib/nostr/domain";
import { useSongFavorites } from "../lib/hooks/useSongFavorites";
import { useAppDataTimeline } from "../lib/nostr/hooks/useRelayTimeline";
import { ShareSongDialog } from "../components/ShareSongDialog";
import { SongMediaDialog } from "../components/SongMediaDialog";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/crate")({
  component: Crate,
});

// ─── Song resolution ──────────────────────────────────────────────────────────

function useSongsFromLists(lists: ParsedSongList[]): {
  songsByAddress: Map<string, ParsedSong>;
  isLoading: boolean;
} {
  const addresses = useMemo(
    () => Array.from(new Set(lists.flatMap((list) => list.songAddresses))),
    [lists],
  );
  const filters: Filter[] = useMemo(() => {
    if (addresses.length === 0) return [];
    const authors = Array.from(
      new Set(addresses.map((a) => a.split(":")[1]).filter(Boolean) as string[]),
    );
    const dTags = Array.from(
      new Set(addresses.map((a) => a.split(":")[2]).filter(Boolean) as string[]),
    );
    return [
      {
        kinds: [SONG_KIND],
        authors,
        "#d": dTags,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(addresses)]);

  const { events, isLoading } = useAppDataTimeline(
    filters.length > 0 ? filters : null,
  );

  const songsByAddress = useMemo(
    () => {
      const songs = events.map((event) => parseSongEvent(event));
      return new Map(
        songs
          .filter((song): song is ParsedSong & { address: string } =>
            Boolean(song.address && addresses.includes(song.address)),
          )
          .map((song) => [song.address, song]),
      );
    },
    [events, addresses],
  );

  return {
    songsByAddress,
    isLoading,
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// ─── Song row ─────────────────────────────────────────────────────────────────

interface SongRowProps {
  song: ParsedSong;
  sourceList: ParsedSongList;
  otherLists: ParsedSongList[];
  onMove: (songAddress: string, fromListId: string, toListId: string) => Promise<void>;
  onRemove: (songAddress: string, listId: string) => Promise<void>;
}

function SongRow({ song, sourceList, otherLists, onMove, onRemove }: SongRowProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const songAddress = song.address;

  const handleMove = async (toListId: string) => {
    if (!sourceList.listId || !songAddress) return;
    setMenuOpen(false);
    await onMove(songAddress, sourceList.listId, toListId);
  };

  const handleRemove = async () => {
    if (!sourceList.listId || !songAddress) return;
    setBusy(true);
    await onRemove(songAddress, sourceList.listId);
    setBusy(false);
  };

  return (
    <>
    <div className="flex items-center gap-3 px-4 py-2.5 border-b-2 border-on-background/10 hover:bg-surface-container-high transition-colors group">
      {/* Thumbnail */}
      <div className="w-10 h-10 shrink-0 bg-on-background/10 border-2 border-on-background/20 flex items-center justify-center overflow-hidden">
        {song.thumb ? (
          <img
            src={song.thumb}
            alt={song.album || song.title}
            className="w-full h-full object-cover"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
          />
        ) : (
          <span className="material-symbols-outlined text-[18px] text-on-background/30">music_note</span>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="font-black text-[13px] uppercase tracking-tighter truncate leading-tight">
          {song.title || "UNKNOWN_TRACK"}
        </p>
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className="text-[10px] text-on-background/55 truncate leading-tight">
            {song.artist || "Unknown Artist"}
            {song.album && <span className="opacity-70"> · {song.album}</span>}
          </p>
          {song.genres.slice(0, 2).map((g) => (
            <span key={g} className="text-[8px] font-black uppercase tracking-widest text-on-background/35 border border-on-background/20 px-1 leading-tight hidden sm:inline">
              {g}
            </span>
          ))}
        </div>
      </div>

      {/* Year + Duration */}
      <div className="shrink-0 hidden sm:flex flex-col items-end gap-0.5">
        {song.releaseYear && (
          <span className="text-[10px] font-bold text-on-background/40 tabular-nums">{song.releaseYear}</span>
        )}
        {song.duration && (
          <span className="text-[9px] font-bold text-on-background/30 tabular-nums">{formatDuration(song.duration)}</span>
        )}
      </div>

      {/* MusicBrainz link */}
      {song.mbid && (
        <a
          href={`https://musicbrainz.org/recording/${song.mbid}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="shrink-0 text-on-background/20 hover:text-on-background/60 transition-colors"
          title="View on MusicBrainz"
        >
          <span className="material-symbols-outlined text-[14px]">open_in_new</span>
        </a>
      )}

      {/* Play audio */}
      {song.audioUrl && (
        <button
          onClick={(e) => { e.stopPropagation(); setEmbedOpen((v) => !v); }}
          className={cn("shrink-0 transition-colors", embedOpen ? "text-primary" : "text-primary/60 hover:text-primary")}
          title={embedOpen ? "Close player" : "Play audio"}
        >
          <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>
            {embedOpen ? "stop_circle" : "play_circle"}
          </span>
        </button>
      )}

      {/* Save audio or video */}
      <button
        onClick={(e) => { e.stopPropagation(); setMediaDialogOpen(true); }}
        className={cn(
          "shrink-0 transition-colors",
          song.audioUrl ? "text-primary/60 hover:text-primary" : "text-on-background/30 hover:text-on-background",
        )}
        title={song.audioUrl ? "Save another copy to Blossom" : "Save audio or video to Blossom"}
      >
        <span className="material-symbols-outlined text-[16px]">auto_fix_high</span>
      </button>

      {/* Share */}
      <button
        onClick={(e) => { e.stopPropagation(); setShareOpen(true); }}
        className="shrink-0 text-on-background/30 hover:text-on-background transition-colors"
        title="Share as Nostr note"
      >
        <span className="material-symbols-outlined text-[16px]">share</span>
      </button>

      {/* Remove */}
      <button
        onClick={(e) => { e.stopPropagation(); handleRemove(); }}
        disabled={busy}
        className="shrink-0 text-destructive/40 hover:text-destructive transition-colors disabled:opacity-40"
        title="Remove from list"
      >
        {busy ? (
          <span className="material-symbols-outlined text-[16px]" style={{ animation: "spin 0.8s linear infinite" }}>sync</span>
        ) : (
          <span className="material-symbols-outlined text-[16px]">remove_circle</span>
        )}
      </button>

      {/* Move to another list — dropdown, only when other lists exist */}
      {otherLists.length > 0 && (
        <div className="relative shrink-0" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="w-7 h-7 flex items-center justify-center text-on-background/30 hover:text-on-background transition-colors"
            title="Move to list"
          >
            <span className="material-symbols-outlined text-[16px]">drive_file_move</span>
          </button>

          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-full mt-1 z-20 bg-background border-2 border-on-background shadow-[4px_4px_0px_0px_rgba(29,28,19,1)] min-w-[160px]">
                <div className="px-3 py-1.5 text-[9px] font-black uppercase tracking-widest text-on-background/40 border-b border-on-background/10">
                  Move to
                </div>
                {otherLists.map((list) => (
                  <button
                    key={list.listId}
                    onClick={() => handleMove(list.listId!)}
                    className="w-full text-left px-3 py-2 text-[11px] font-bold uppercase tracking-tight hover:bg-on-background hover:text-surface transition-colors flex items-center gap-2"
                  >
                    <span className="material-symbols-outlined text-[14px]">drive_file_move</span>
                    {list.name || "Unnamed list"}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
    {mediaDialogOpen && (
      <SongMediaDialog song={song} onClose={() => setMediaDialogOpen(false)} />
    )}
    {shareOpen && (
      <ShareSongDialog song={song} onClose={() => setShareOpen(false)} />
    )}
    {embedOpen && song.audioUrl && (
      <div className="border-t-2 border-on-background/10 bg-black px-4 py-2 flex items-center gap-3">
        <video controls autoPlay src={song.audioUrl} className="flex-1 max-h-64 min-w-0" />
        <button
          onClick={() => setEmbedOpen(false)}
          className="shrink-0 text-white/40 hover:text-white transition-colors"
        >
          <span className="material-symbols-outlined text-[12px]">close</span>
        </button>
      </div>
    )}
  </>
  );
}

// ─── List panel ───────────────────────────────────────────────────────────────

interface SongListPanelProps {
  list: ParsedSongList;
  allLists: ParsedSongList[];
  songs: ParsedSong[];
  isLoading: boolean;
  onMove: (songAddress: string, fromListId: string, toListId: string) => Promise<void>;
  onRemove: (songAddress: string, listId: string) => Promise<void>;
}

function SongListPanel({
  list,
  allLists,
  songs,
  isLoading,
  onMove,
  onRemove,
}: SongListPanelProps) {
  const count = getSongListSongCount(list);
  const otherLists = allLists.filter((l) => l.listId !== list.listId);

  return (
    <div className="border-4 border-on-background shadow-[6px_6px_0px_0px_rgba(29,28,19,1)]">
      <div className="flex items-center justify-between px-4 py-3 bg-on-background text-surface">
        <div>
          <h2 className="font-black text-base uppercase tracking-tighter font-headline leading-none">
            {list.name || "UNNAMED_LIST"}
          </h2>
          {list.description && (
            <p className="text-[10px] opacity-60 mt-0.5">{list.description}</p>
          )}
        </div>
        <span className="text-[10px] font-bold opacity-50 tabular-nums">{count}_TRACKS</span>
      </div>

      {isLoading && count > 0 ? (
        <div>
          {Array.from({ length: Math.min(count, 5) }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-2.5 border-b-2 border-on-background/10 animate-pulse">
              <div className="w-10 h-10 shrink-0 bg-on-background/10" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 bg-on-background/10 w-2/3" />
                <div className="h-2 bg-on-background/10 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : songs.length > 0 ? (
        <div>
          {songs.map((song) => (
            <SongRow
              key={song.id || song.songId}
              song={song}
              sourceList={list}
              otherLists={otherLists}
              onMove={onMove}
              onRemove={onRemove}
            />
          ))}
        </div>
      ) : count === 0 ? (
        <div className="px-4 py-8 text-center">
          <span className="material-symbols-outlined text-4xl text-on-background/20 block mb-2">music_off</span>
          <p className="text-[10px] font-bold uppercase tracking-widest text-on-background/40">NO_TRACKS_YET</p>
          <p className="text-[9px] text-on-background/30 mt-1">Like a song while listening to add it here</p>
        </div>
      ) : (
        <div className="px-4 py-4 text-[10px] text-on-background/40 uppercase tracking-widest text-center">
          TRACKS_CURRENTLY_UNAVAILABLE
        </div>
      )}
    </div>
  );
}

// ─── Inline create form ───────────────────────────────────────────────────────

function CreateListForm({ onCreate, onCancel }: { onCreate: (name: string) => Promise<void>; onCancel: () => void }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    await onCreate(name.trim());
    setBusy(false);
  };

  return (
    <form onSubmit={handleSubmit} className="border-4 border-on-background bg-surface-container-high p-4 flex items-center gap-3 shadow-[6px_6px_0px_0px_rgba(29,28,19,1)]">
      <input
        autoFocus
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="LIST_NAME..."
        maxLength={60}
        className="flex-1 bg-transparent font-black uppercase tracking-tighter text-sm outline-none border-b-2 border-on-background py-1 placeholder:text-on-background/25 font-headline"
      />
      <button
        type="submit"
        disabled={!name.trim() || busy}
        className="border-2 border-on-background px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:bg-on-background hover:text-surface transition-colors disabled:opacity-40"
      >
        {busy ? "CREATING..." : "CREATE"}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="border-2 border-on-background/30 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:border-on-background transition-colors"
      >
        CANCEL
      </button>
    </form>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function Crate() {
  // Auth check uses applesauce-native useCurrentAccount. The previous version
  // migration, so this route showed AUTHENTICATION_REQUIRED even when logged in.
  const currentUser = useCurrentAccount();
  const isLoggedIn = !!currentUser;
  const { songLists, isLoading, createList, moveSong, removeSongFromList } =
    useSongFavorites();
  const { songsByAddress, isLoading: songsLoading } =
    useSongsFromLists(songLists);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const totalTracks = useMemo(
    () => songLists.reduce((n, l) => n + getSongListSongCount(l), 0),
    [songLists],
  );

  const handleCreate = async (name: string) => {
    await createList(name);
    setShowCreateForm(false);
  };

  if (!isLoggedIn) {
    return (
      <div className="space-y-6">
        <PageHeader totalTracks={0} />
        <div className="border-4 border-on-background bg-surface-container-high shadow-[6px_6px_0px_0px_rgba(29,28,19,1)] p-12 text-center">
          <span className="material-symbols-outlined text-6xl text-on-background/20 block mb-4">lock</span>
          <div className="text-xl font-black uppercase tracking-tight mb-2 font-headline">AUTHENTICATION_REQUIRED</div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-on-background/50">CONNECT_TO_ACCESS_YOUR_CRATE</div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader totalTracks={0} />
        <div className="border-4 border-on-background shadow-[6px_6px_0px_0px_rgba(29,28,19,1)] animate-pulse">
          <div className="h-12 bg-on-background/10" />
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-2.5 border-b-2 border-on-background/10">
              <div className="w-10 h-10 bg-on-background/10 shrink-0" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 bg-on-background/10 w-2/3" />
                <div className="h-2 bg-on-background/10 w-1/3" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <PageHeader
          totalTracks={totalTracks}
          showCreateForm={showCreateForm}
          onNewList={() => setShowCreateForm((v) => !v)}
        />
        {showCreateForm && (
          <CreateListForm onCreate={handleCreate} onCancel={() => setShowCreateForm(false)} />
        )}
      </div>

      {songLists.length === 0 && !showCreateForm ? (
        <div className="border-4 border-on-background bg-surface-container-high shadow-[6px_6px_0px_0px_rgba(29,28,19,1)] p-12 text-center">
          <span className="material-symbols-outlined text-6xl text-on-background/20 block mb-4">album</span>
          <div className="text-xl font-black uppercase tracking-tight mb-2 font-headline">CRATE_IS_EMPTY</div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-on-background/50 mb-2">
            LIKE_A_SONG_WHILE_LISTENING_TO_START_YOUR_COLLECTION
          </div>
          <div className="text-[9px] text-on-background/30">Tap ★ next to the now-playing track in the player bar</div>
        </div>
      ) : (
        songLists.map((list) => (
          <SongListPanel
            key={list.listId || list.pubkey}
            list={list}
            allLists={songLists}
            songs={list.songAddresses
              .map((address) => songsByAddress.get(address))
              .filter((song): song is ParsedSong => Boolean(song))}
            isLoading={songsLoading}
            onMove={moveSong}
            onRemove={removeSongFromList}
          />
        ))
      )}
    </div>
  );
}

// ─── Page header ──────────────────────────────────────────────────────────────

function PageHeader({
  totalTracks,
  showCreateForm,
  onNewList,
}: {
  totalTracks: number;
  showCreateForm?: boolean;
  onNewList?: () => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-baseline gap-4">
        <h1 className="text-2xl sm:text-4xl md:text-6xl font-black uppercase tracking-tighter font-headline">CRATE</h1>
        <div className={cn("h-2 flex-grow bg-on-background", totalTracks === 0 && "opacity-20")} />
        {totalTracks > 0 && (
          <span className="font-bold text-primary text-sm hidden md:block tracking-widest uppercase">{totalTracks}_TRACKS</span>
        )}
      </div>
      {onNewList && (
        <button
          onClick={onNewList}
          className={cn(
            "border-2 border-on-background px-4 py-2 text-[10px] font-black uppercase tracking-widest transition-colors flex items-center gap-1.5",
            showCreateForm
              ? "bg-on-background text-surface"
              : "bg-surface hover:bg-on-background hover:text-surface",
          )}
        >
          <span className="material-symbols-outlined text-[14px]">{showCreateForm ? "close" : "add"}</span>
          {showCreateForm ? "CANCEL" : "NEW_LIST"}
        </button>
      )}
    </div>
  );
}
